import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useStripe } from '../contexts/StripeContext';
import PaymentButton from '../components/PaymentButton';
import CreditsDisplay from '../components/CreditsDisplay';

// This should match your Stripe price ID in the backend
const PRICE_ID = process.env.REACT_APP_STRIPE_PRICE_ID || 'price_1RKpH4L7dru6VkDz7F2w9cMb';

const PaymentPage: React.FC = () => {
  const { user } = useAuth();
  const { } = useStripe(); // We still need the context for the PaymentButton
  const navigate = useNavigate();

  // Redirect to login if not authenticated
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return null; // Will redirect to login
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Purchase Credits</h1>
        
        <div className="bg-white shadow-md rounded-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Your Credits</h2>
            <CreditsDisplay className="text-lg" />
          </div>
          
          <p className="text-gray-600 mb-4">
            Credits are required to generate research reports. Each report costs 1 credit.
            New users automatically receive 1 free credit to try the service.
          </p>
          
          {/* Free Credit Info */}
          <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700">
                  <span className="font-medium">Welcome bonus:</span> All new users receive 1 free research credit upon signing up. If you've used your free credit, purchase more below to continue using the service.
                </p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-200 my-4"></div>
          
          <div className="mt-6">
            <h3 className="text-lg font-medium mb-4">Purchase Options</h3>
            
            <div className="grid gap-4 md:grid-cols-2">
              <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <h4 className="text-lg font-medium mb-2">Single Report</h4>
                <p className="text-gray-600 mb-4">Generate one research report</p>
                <div className="flex justify-between items-center">
                  <span className="text-xl font-bold">$5.99</span>
                  <PaymentButton 
                    priceId={PRICE_ID} 
                    label="Purchase"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                All payments are processed securely through Stripe. Your payment information is never stored on our servers.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
